import { useMemo } from 'react';
import { AudioFile } from '../services/api';

interface ExpiringFilesResult {
  expiringFiles: AudioFile[];
  count: number;
  hasExpiringFiles: boolean;
}

/**
 * Hook to calculate which files are expiring within 24 hours
 *
 * @param files - Array of AudioFile objects (should include ttl field)
 * @returns Object containing expiring files, count, and boolean flag
 */
export const useExpiringFiles = (files: AudioFile[]): ExpiringFilesResult => {
  const result = useMemo(() => {
    // Current time in Unix timestamp (seconds)
    const now = Math.floor(Date.now() / 1000);

    // 24 hours from now (86400 seconds)
    const threshold = now + (24 * 60 * 60);

    // Filter files that:
    // 1. Have a ttl field (temporary files only)
    // 2. Are not yet expired (ttl > now)
    // 3. Will expire within 24 hours (ttl <= threshold)
    const expiring = files.filter(file =>
      file.ttl &&
      file.ttl > now &&
      file.ttl <= threshold
    );

    return {
      expiringFiles: expiring,
      count: expiring.length,
      hasExpiringFiles: expiring.length > 0
    };
  }, [files]);

  return result;
};
